import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';

import MultiTableRenderer from '../../../../components/TablesDetailRender';
import ErrorBoundary from '../../../../components/ErrorBoundary';
import ToastNotification from '../../../../components/toast_notification';
import TabelaElegibilidade from '../../../../components/tabela_elegibilidade';
import CancelContractModal from '../../../../components/CancelContractModal';
import CancelLoteModal from '../../../../components/CancelLoteModal';
import TabelaDinamica from '../../../../components/tabela_dinamica';
import { PermissionGate } from '../../../../contexts/PermissionGate';
import SkeletonIndicators from '../../../../components/SkeletonIndicators';
import SkeletonHeaderIndicators from '../../../../components/SkeletonHeaderIndicators';

import { useApi } from '../../../../hooks/ApiGateway';
import { API_CONFIG } from '../../../../config/api';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faRedo, faChevronLeft, faDownload } from '@fortawesome/free-solid-svg-icons';

type Widget<TData = unknown, TConfig = unknown> = { data: TData; config: TConfig };
type DataType<TData = any, TConfig = any> = { widgets: Array<Widget<TData, TConfig>> };

interface Column {
  id: string;
  type: string;
  label: string;
  format?: string;
  currency?: string;
  decimals?: number;
}

interface TableData {
  columns: Column[];
  rows: Record<string, unknown>[];
}

type ProcedureItem = Record<string, TableData>;

type ToastState = { show: boolean; status: number; message: string };
function safeJsonParse<T>(value: unknown): T | null {
  try {
    if (typeof value === "string") {
      const sanitized = value
        .replace(/\bNaN\b/g, "null")
        .replace(/\bInfinity\b/g, "null")
        .replace(/\b-Infinity\b/g, "null");

      return JSON.parse(sanitized) as T;
    }

    if (typeof value === "object" && value !== null) {
      return value as T;
    }

    return null;
  } catch {
    return null;
  }
}
function parseApiResult<T = any>(result: unknown): T {
  let current: unknown = (result as any)?.data ?? (result as any)?.body ?? result;

  while (true) {
    if (typeof current === "string") {
      const parsed = safeJsonParse(current);

      if (parsed === null) {
        throw new Error("String não é um JSON válido");
      }

      current = parsed;
      continue;
    }

    if (
      typeof current === "object" &&
      current !== null &&
      "body" in current
    ) {
      current = (current as any).body;
      continue;
    }

    break;
  }

  if (typeof current !== "object" || current === null) {
    throw new Error("Resultado final não é um JSON válido");
  }

  return current as T;
}

const DetailsCockpit = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { id_produto } = (location.state as { id_produto?: string } | null) || {};
  const id_usuario = useMemo(() => localStorage.getItem('id_usuario') ?? '', []);
  const is_approver = true;

  const { makeGetRequest } = useApi<any>();

  const [tables, setTables] = useState<ProcedureItem[]>([]);
  const [rejectedData, setRejectedData] = useState<DataType>({ widgets: [] });
  const [aceitosData, setAceitosData] = useState<DataType>({ widgets: [] });

  const [activeTab, setActiveTab] = useState<'all' | 'resumototal' | 'resumocrit'>('all');
  const [disableOverviewAndDetails, setDisableOverviewAndDetails] = useState(false);

  const [showModalCancelContrato, setShowModalCancelContrato] = useState(false);
  const [showModalCancelLote, setShowModalCancelLote] = useState(false);

  const [isLoadingTables, setIsLoadingTables] = useState(true);
  const [isLoadingAceitos, setIsLoadingAceitos] = useState(true);
  const [isLoadingRejeitados, setIsLoadingRejeitados] = useState(true);

  const [isApproving, setIsApproving] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const isBusy = isApproving || isCanceling || isReprocessing;

  const [toast, setToast] = useState<ToastState>({ show: false, status: 200, message: '' });
  const showToast = useCallback((status: number, message: string) => {
    setToast({ show: true, status, message });
  }, []);
  const closeToast = useCallback(() => setToast((t) => ({ ...t, show: false })), []);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchTables = useCallback(async () => {
    if (!id || !id_produto || !id_usuario) return;

    try {
      setIsLoadingTables(true);
      const url = `${API_CONFIG.LAMBDA_URL}?user_id=${encodeURIComponent(
        id_usuario
      )}&id_produto=${encodeURIComponent(id_produto)}&p_id_remessa=${encodeURIComponent(id)}`;

      const result = await makeGetRequest(url);
      const payload = parseApiResult<any>(result);
      const statusCode =
        payload?.data?.statusCode ??
        payload?.statusCode ??
        payload?.data?.data?.statusCode ??
        null;

      if (statusCode !== 200) {
        navigate('/page-not-found');
        return;
      }

      const procedures =
        payload?.data?.body?.procedures ??
        payload?.body?.procedures ??
        payload?.procedures ??
        null;

      if (procedures && Array.isArray(procedures)) {
        if (mountedRef.current) setTables(procedures);
      } else {
        console.warn('Dados de procedures inválidos ou ausentes');
        if (mountedRef.current) setTables([]);
      }
    } catch (error) {
      console.error('Erro ao buscar dados (tables):', error);
      if (mountedRef.current) setTables([]);
      navigate('/page-not-found');
    } finally {
      if (mountedRef.current) setIsLoadingTables(false);
    }
  }, [id, id_produto, id_usuario, makeGetRequest, navigate]);

  const fetchAceitos = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoadingAceitos(true);
      const result = await makeGetRequest(
        `${API_CONFIG.BASE_URL}/cessao/detalhe/base-aceita?id_remessa=${encodeURIComponent(id)}`
      );
      const payload = parseApiResult<any>(result);
      const normalized = (payload?.widgets ? payload : payload?.data ?? payload) as DataType;
      if (mountedRef.current) setAceitosData(normalized ?? { widgets: [] });
    } catch (error) {
      console.error('Erro ao buscar aceitos:', error);
      if (mountedRef.current) setAceitosData({ widgets: [] });
    } finally {
      if (mountedRef.current) setIsLoadingAceitos(false);
    }
  }, [id, makeGetRequest]);

  const fetchRejeitados = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoadingRejeitados(true);
      const result = await makeGetRequest(
        `${API_CONFIG.BASE_URL}/cessao/detalhe/base-rejeitada?id_remessa=${encodeURIComponent(id)}`
      );
      const payload = parseApiResult<any>(result);
      const normalized = (payload?.widgets ? payload : payload?.data ?? payload) as DataType;

      if (mountedRef.current) setRejectedData(normalized ?? { widgets: [] });
    } catch (error) {
      console.error('Erro ao buscar rejeitados:', error);
      if (mountedRef.current) setRejectedData({ widgets: [] });
    } finally {
      if (mountedRef.current) setIsLoadingRejeitados(false);
    }
  }, [id, makeGetRequest]);

  useEffect(() => {
    if (!id) return;
    fetchTables();
    fetchAceitos();
    fetchRejeitados();
  }, [id, fetchTables, fetchAceitos, fetchRejeitados]);

  const hasOverviewData = useMemo(
    () => (aceitosData.widgets?.[0]?.data?.length ?? 0) > 0,
    [aceitosData]
  );

  useEffect(() => {
    if (isLoadingAceitos) return;
    const shouldDisable = !hasOverviewData;
    setDisableOverviewAndDetails(shouldDisable);
    if (shouldDisable) setActiveTab('resumocrit');
  }, [isLoadingAceitos, hasOverviewData]);

  async function runAction(opts: {
    setBusy: (v: boolean) => void;
    pendingMessage: string;
    url: string;
    body: unknown;
    successRedirect?: string;
  }) {
    const { setBusy, pendingMessage, url, body, successRedirect } = opts;

    if (!id) {
      showToast(400, 'ID da remessa não encontrado.');
      return;
    }

    setBusy(true);
    showToast(202, pendingMessage);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      const message =
        typeof data?.message === 'string' && data.message.trim() !== ''
          ? data.message
          : 'Erro durante o processamento, tente novamente em alguns minutos. Se persistir, contate o suporte.';

      const status = Number(data?.status ?? (response.ok ? 200 : 500)) || (response.ok ? 200 : 500);

      showToast(status, message);

      if (response.ok && successRedirect) {
        setTimeout(() => navigate(successRedirect), 2000);
      }
    } catch (error) {
      console.error('Erro na requisição:', error);
      showToast(500, error instanceof Error ? error.message : 'Ocorreu um erro ao processar a solicitação.');
    } finally {
      setBusy(false);
    }
  }

  const handleApprove = useCallback(async () => {
    await runAction({
      setBusy: setIsApproving,
      pendingMessage: 'Aprovação em andamento...',
      url: `${API_CONFIG.BASE_URL}/acoes-remessa/aprovar?id_remessa=${encodeURIComponent(id ?? '')}`,
      body: { id_usuario },
    });
  }, [id, id_usuario]);

  const handleReprocess = useCallback(async () => {
    await runAction({
      setBusy: setIsReprocessing,
      pendingMessage: 'Reprocessamento em andamento...',
      url: `${API_CONFIG.BASE_URL}/acoes-remessa/reprocessar?id_remessa=${encodeURIComponent(id ?? '')}`,
      body: { id_usuario },
      successRedirect: '/aquisicao/operacoes',
    });
  }, [id, id_usuario]);

  const handleDownload = useCallback(async () => {
    if (!id) {
      showToast(400, 'ID da remessa não encontrado.');
      return;
    }

    setIsReprocessing(true);
    showToast(202, 'Inicializando download...');

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/acoes-remessa/download_indicadores?id_remessa=${encodeURIComponent(id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_usuario }),
        }
      );

      const raw = await response.json();
      const data = parseApiResult<any>(raw);

      const presignedUrl = data?.message;

      if (!response.ok || !presignedUrl) {
        throw new Error(
          data?.message || 'Não foi possível obter a URL de download.'
        );
      }

      const link = document.createElement('a');
      link.href = presignedUrl;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast(200, 'Download iniciado com sucesso!');
    } catch (error) {
      console.error('Erro no download:', error);
      showToast(
        500,
        error instanceof Error ? error.message : 'Erro ao baixar arquivo.'
      );
    } finally {
      setIsReprocessing(false);
    }
  }, [id, id_usuario]);

  const handleSubmitCancelContrato = useCallback(
    async (contratos: { numero_documento: string; razao_cancelamento: string }[]) => {
      setShowModalCancelContrato(false);
      await runAction({
        setBusy: setIsCanceling,
        pendingMessage: 'Cancelamento em andamento...',
        url: `${API_CONFIG.BASE_URL}/acoes-remessa/cancelar?id_remessa=${encodeURIComponent(id ?? '')}`,
        body: { id_usuario, contratos },
        successRedirect: '/aquisicao/operacoes',
      });
    },
    [id, id_usuario]
  );

  const handleSubmitCancelLote = useCallback(
    async (razao_cancelamento: string) => {
      setShowModalCancelLote(false);
      await runAction({
        setBusy: setIsCanceling,
        pendingMessage: 'Cancelamento em andamento...',
        url: `${API_CONFIG.BASE_URL}/acoes-remessa/cancelar-remessa?id_remessa=${encodeURIComponent(id ?? '')}`,
        body: { id_usuario, razao_cancelamento },
        successRedirect: '/aquisicao/operacoes',
      });
    },
    [id, id_usuario]
  );

  const isLoadingUI = isLoadingTables || isLoadingAceitos || isLoadingRejeitados;

  const rejectedWidget = rejectedData?.widgets?.[0];
  const aceitosWidget = aceitosData?.widgets?.[0];

  return (
    <ErrorBoundary>
      {isLoadingUI ? (
        <SkeletonHeaderIndicators />
      ) : (
        <>
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-link p-0 mr-2"
                onClick={() => navigate(-1)}
                style={{ textDecoration: 'none', fontSize: '1.2rem' }}
                type="button"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="mr-1" color="black" />
              </button>
              <h1 className="page-heading mb-0">Detalhes da Cessão</h1>
            </div>

            <div className="text-right">
              <PermissionGate required="APPROVE_ASSIGNMENT">
                <button
                  className="btn btn-sm btn-acao mr-2 text-white"
                  onClick={handleApprove}
                  disabled={!is_approver || isBusy}
                  type="button"
                >
                  <FontAwesomeIcon icon={faCheck} className="mr-1" />
                  Aprovar
                </button>
              </PermissionGate>

              <PermissionGate required="CANCEL_BATCH">
                <button
                  className="btn btn-sm btn-acao mr-2 text-white"
                  onClick={() => setShowModalCancelLote(true)}
                  disabled={!is_approver || isBusy}
                  type="button"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Cancelar lote
                </button>
              </PermissionGate>

              <PermissionGate required="CANCEL_CONTRACT">
                <button
                  className="btn btn-sm btn-acao mr-2 text-white"
                  onClick={() => setShowModalCancelContrato(true)}
                  disabled={!is_approver || isBusy}
                  type="button"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Cancelar contrato
                </button>
              </PermissionGate>

              <PermissionGate required="REPROCESS">
                <button
                  className="btn btn-sm btn-acao mr-2 text-white"
                  onClick={handleReprocess}
                  disabled={!is_approver || isBusy}
                  type="button"
                >
                  <FontAwesomeIcon icon={faRedo} className="mr-1" />
                  Reprocessar
                </button>
              </PermissionGate>

              <PermissionGate required="DOWNLOAD_INDICATORS">
                <button
                  className="btn btn-sm btn-acao mr-2 text-white"
                  onClick={handleDownload}
                  disabled={!is_approver || isBusy}
                  type="button"
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                  Baixar base
                </button>
              </PermissionGate>
            </div>
          </div>

          <div className="px-1">
            <ul className="nav hana-tabs">
              {[
                { key: 'all' as const, label: 'Overview' },
                { key: 'resumototal' as const, label: 'Detalhes' },
                { key: 'resumocrit' as const, label: 'Critérios' },
              ].map((tab) => {
                const isDisabled =
                  disableOverviewAndDetails && (tab.key === 'all' || tab.key === 'resumototal');

                return (
                  <li className="nav-item" key={tab.key}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.key ? 'active' : ''} ${
                        isDisabled ? 'disabled' : ''
                      }`}
                      onClick={() => !isDisabled && setActiveTab(tab.key)}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                    >
                      {tab.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      {isLoadingUI ? (
        <SkeletonIndicators />
      ) : (
        <>
          {activeTab === 'all' && <MultiTableRenderer tables={tables} />}

          {activeTab === 'resumocrit' && rejectedWidget && (
            <TabelaElegibilidade data={rejectedWidget.data} config={rejectedWidget.config} />
          )}

          {activeTab === 'resumototal' && aceitosWidget && (
            <TabelaDinamica
              data={{
                columns: aceitosWidget.config?.columns ?? [],
                rows: aceitosWidget.data ?? [],
              }}
              config={aceitosWidget.config}
              title="Detalhes dos Ativos"
              subtitle="Utilize o campo abaixo para pesquisar por uma transação específica"
            />
          )}
        </>
      )}

      {toast.show && (
        <ToastNotification
          key={`${toast.status}-${toast.message}`}
          status={toast.status}
          message={toast.message}
          onClose={closeToast}
        />
      )}

      <CancelContractModal
        show={showModalCancelContrato}
        onHide={() => setShowModalCancelContrato(false)}
        onSubmit={handleSubmitCancelContrato}
        idDocumento=""
      />

      <CancelLoteModal
        show={showModalCancelLote}
        onHide={() => setShowModalCancelLote(false)}
        onSubmit={handleSubmitCancelLote}
      />
    </ErrorBoundary>
  );
};

export default DetailsCockpit;
